import { useState, type ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import type { LucideIcon } from 'lucide-react';

export interface HoverTabSection {
  icon: LucideIcon;
  title: string;
  content: ReactNode;
}

interface HoverTabSectionsProps {
  sections: HoverTabSection[];
}

const HoverTabSections = ({ sections }: HoverTabSectionsProps) => {
  const [active, setActive] = useState(0);

  if (sections.length === 0) return null;
  const current = sections[Math.min(active, sections.length - 1)];

  return (
    <Card>
      <CardContent className="p-6">
        <div
          className="grid gap-3 mb-6"
          style={{ gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))` }}
        >
          {sections.map((section, idx) => {
            const Icon = section.icon;
            const isActive = idx === active;
            return (
              <button
                key={idx}
                type="button"
                onMouseEnter={() => setActive(idx)}
                onFocus={() => setActive(idx)}
                onClick={() => setActive(idx)}
                className={`aspect-square flex flex-col items-center justify-center gap-3 p-4 rounded-lg border-2 transition-all text-center ${
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground shadow-md'
                    : 'border-border bg-muted hover:border-primary/50'
                }`}
              >
                <Icon className="h-10 w-10 flex-shrink-0" />
                <span className="text-base font-semibold leading-tight">{section.title}</span>
              </button>
            );
          })}
        </div>
        <div className="p-5 bg-muted rounded-lg border-l-4 border-primary animate-fade-in">
          <h3 className="text-lg font-bold mb-3">{current.title}</h3>
          <div>{current.content}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HoverTabSections;
